import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import SEO from '../components/SEO';
import { paymentStoreName } from '../utils/iap';

// ── Section & topic definitions (drives both sidebar and anchor IDs) ──────────
const NAV = [
  { id: 'getting-started',    icon: 'bi-rocket-takeoff-fill', label: 'Getting Started' },
  { id: 'install-pwa',        icon: 'bi-phone-fill',          label: 'Install the App (PWA)' },
  { id: 'child-profiles',     icon: 'bi-people-fill',         label: 'Child Profiles' },
  { id: 'generate-stories',   icon: 'bi-magic',               label: 'Generating Stories' },
  { id: 'story-library',      icon: 'bi-book-half',           label: 'Story Library' },
  { id: 'reading-stories',    icon: 'bi-book-fill',           label: 'Reading Stories' },
  { id: 'ai-narration',       icon: 'bi-headphones',          label: 'AI Narration' },
  { id: 'voice-cloning',      icon: 'bi-mic-fill',            label: 'Voice Cloning' },
  { id: 'photo-illustrations',icon: 'bi-image-fill',          label: 'Photo Illustrations' },
  { id: 'approval-queue',     icon: 'bi-eye-fill',            label: 'Story Approvals' },
  { id: 'story-series',       icon: 'bi-collection-play-fill',label: 'Story Series' },
  { id: 'community',          icon: 'bi-globe2',              label: 'Community' },
  { id: 'reading-reports',    icon: 'bi-bar-chart-fill',      label: 'Reading Reports' },
  { id: 'notifications',      icon: 'bi-bell-fill',           label: 'Notifications' },
  { id: 'account-management', icon: 'bi-person-fill',         label: 'Account Management' },
  { id: 'credits-plans',      icon: 'bi-credit-card-fill',    label: 'Credits & Plans' },
  { id: 'referral-program',   icon: 'bi-gift-fill',           label: 'Referral Program' },
  { id: 'gift-stories',       icon: 'bi-envelope-heart-fill', label: 'Gift Stories' },
  { id: 'tips',               icon: 'bi-lightbulb-fill',      label: 'Tips & Best Practices' },
];

// ── Reusable content building blocks ─────────────────────────────────────────
const SectionHeader = ({ id, icon, title, subtitle }) => (
  <div className="d-flex align-items-start gap-3 mb-4">
    <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
      <i className={`bi ${icon} text-white`} style={{ fontSize: 22 }}></i>
    </div>
    <div>
      <h2 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: 22 }} id={id}>{title}</h2>
      {subtitle && <p className="text-muted mb-0" style={{ fontSize: 14 }}>{subtitle}</p>}
    </div>
  </div>
);

const Step = ({ n, title, children }) => (
  <div className="d-flex gap-3 mb-4">
    <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0"
      style={{ width: 28, height: 28, background: '#6f42c1', fontSize: 13, marginTop: 2 }}>
      {n}
    </div>
    <div>
      {title && <div className="fw-semibold mb-1" style={{ color: 'var(--text-main)' }}>{title}</div>}
      <div className="text-muted" style={{ fontSize: 14, lineHeight: 1.65 }}>{children}</div>
    </div>
  </div>
);

const Tip = ({ icon = 'bi-lightbulb-fill', color = '#6f42c1', bg = '#f0e8ff', children }) => (
  <div className="rounded-3 p-3 mb-3 d-flex gap-2 align-items-start"
    style={{ background: bg, border: `1px solid ${color}22` }}>
    <i className={`bi ${icon} flex-shrink-0 mt-1`} style={{ color, fontSize: 15 }}></i>
    <div style={{ fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.6 }}>{children}</div>
  </div>
);

const Note = ({ children }) => (
  <Tip icon="bi-info-circle-fill" color="#0d6efd" bg="#eff6ff">{children}</Tip>
);

const Warning = ({ children }) => (
  <Tip icon="bi-exclamation-triangle-fill" color="#fd7e14" bg="#fff8f0">{children}</Tip>
);

const PlanBadge = ({ plan }) => {
  const styles = {
    free:   { bg: 'var(--header-bg)', color: 'var(--text-muted2)', label: 'Free' },
    basic:  { bg: '#ede9fe', color: '#6f42c1', label: 'Basic' },
    family: { bg: '#fff3e0', color: '#e65c00', label: 'Family' },
  };
  const s = styles[plan] || styles.free;
  return (
    <span className="badge rounded-pill fw-semibold" style={{ background: s.bg, color: s.color, fontSize: 11 }}>
      {s.label} Plan
    </span>
  );
};

const H3 = ({ children }) => (
  <h3 className="fw-bold mt-4 mb-3" style={{ color: 'var(--text-main)', fontSize: 16 }}>{children}</h3>
);

const TableRow = ({ label, free, basic, family }) => (
  <tr>
    <td className="fw-semibold" style={{ color: 'var(--text-main)', fontSize: 13 }}>{label}</td>
    <td className="text-center" style={{ fontSize: 13 }}>{free}</td>
    <td className="text-center" style={{ fontSize: 13 }}>{basic}</td>
    <td className="text-center" style={{ fontSize: 13 }}>{family}</td>
  </tr>
);

const Check = () => <i className="bi bi-check-circle-fill" style={{ color: '#198754' }}></i>;
const Cross = () => <i className="bi bi-x-circle-fill" style={{ color: '#dc2626' }}></i>;

// ── Main page ─────────────────────────────────────────────────────────────────
const GuidePage = () => {
  const [activeId, setActiveId] = useState('getting-started');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const contentRef = useRef(null);

  // Track active section via IntersectionObserver
  useEffect(() => {
    const sections = NAV.map(s => document.getElementById(s.id)).filter(Boolean);
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length) {
          // Pick the topmost visible section
          const top = visible.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          setActiveId(top.target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    sections.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setMobileNavOpen(false);
  };

  return (
    <>
      <SEO
        title="Help Guide"
        description="Everything you need to know about Dreamy Bedtime — from generating your first story to managing child profiles, narration, voice cloning, and more."
        canonical="/guide"
      />
      <Navbar />

      {/* ── Hero ── */}
      <div style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', paddingTop: 56, paddingBottom: 56 }}>
        <div className="container text-center text-white">
          <div className="mb-3" style={{ fontSize: 48 }}>📖</div>
          <h1 className="fw-bold mb-2" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 36 }}>
            Help Guide
          </h1>
          <p className="mb-0" style={{ opacity: 0.85, fontSize: 16, maxWidth: 520, margin: '0 auto' }}>
            Everything you need to know about Dreamy Bedtime — step by step.
          </p>
        </div>
      </div>

      <div className="container py-5" style={{ maxWidth: 1100 }}>
        <div className="row g-4">

          {/* ── Left sidebar nav ── */}
          <div className="col-lg-3">
            {/* Mobile nav toggle */}
            <div className="d-lg-none mb-3">
              <button
                className="btn w-100 fw-semibold d-flex align-items-center justify-content-between"
                onClick={() => setMobileNavOpen(o => !o)}
                style={{ background: 'var(--purple-section)', color: 'var(--text-main)', borderRadius: 12, padding: '12px 16px' }}>
                <span><i className="bi bi-list-ul me-2"></i>Guide Topics</span>
                <i className={`bi bi-chevron-${mobileNavOpen ? 'up' : 'down'}`}></i>
              </button>
            </div>

            {/* Nav list */}
            <div
              className={`${mobileNavOpen ? 'd-block' : 'd-none'} d-lg-block`}
              style={{ position: 'sticky', top: 80 }}>
              <div className="card border-0 shadow-sm p-2" style={{ borderRadius: 16 }}>
                {NAV.map(item => (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className="btn text-start d-flex align-items-center gap-2 w-100 mb-1"
                    style={{
                      borderRadius: 10,
                      padding: '8px 12px',
                      fontSize: 13.5,
                      fontWeight: activeId === item.id ? 700 : 500,
                      background: activeId === item.id ? '#f0e8ff' : 'transparent',
                      color: activeId === item.id ? '#6f42c1' : 'var(--text-muted2)',
                      transition: 'all 0.15s',
                    }}>
                    <i className={`bi ${item.icon} flex-shrink-0`} style={{ fontSize: 14, color: activeId === item.id ? '#6f42c1' : '#999' }}></i>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Content ── */}
          <div className="col-lg-9" ref={contentRef}>

            {/* ─── GETTING STARTED ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="getting-started" icon="bi-rocket-takeoff-fill" title="Getting Started"
                subtitle="Everything you need to know to start using Dreamy Bedtime in minutes." />

              <H3>What is Dreamy Bedtime?</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Dreamy Bedtime is an AI-powered bedtime story generator for families. You create a child profile —
                including their name, age, interests, and values you want stories to reinforce — and the app generates
                personalised, original bedtime stories just for them. Stories can be narrated aloud, illustrated with
                your child's face, and organised into chapter series. Every story is unique and never repeated.
              </p>

              <H3>Creating Your Account</H3>
              <Step n={1} title="Sign up for free">
                Go to the homepage and click <strong>Get Started Free</strong>. Enter your name, email address, and
                choose a password. No credit card is required to start.
              </Step>
              <Step n={2} title="Verify your email">
                Check your inbox for a verification email and click the link inside. This keeps your account secure.
              </Step>
              <Step n={3} title="Complete onboarding">
                After sign-up you'll be taken through a short onboarding flow where you create your first child profile.
                This sets up everything the app needs to generate your first story.
              </Step>

              <H3>Your First Story</H3>
              <Step n={1} title="Go to Generate Story">
                From the sidebar click <strong>Generate Story</strong> or use the button on your Dashboard.
              </Step>
              <Step n={2} title="Pick a child profile">
                Select the child this story is for. The app uses their age, interests, and preferences to tailor the
                story content.
              </Step>
              <Step n={3} title="Customise (optional)">
                Adjust tone, length, language, or add a special occasion like a birthday or holiday. You can also just
                leave the defaults — they produce great stories.
              </Step>
              <Step n={4} title="Generate">
                Click <strong>Generate Story</strong>. The story is created in the background — you'll see a loading
                screen with live status messages. Most stories are ready in 15–30 seconds.
              </Step>
              <Step n={5} title="Read & narrate">
                Once ready, the Story Reader opens automatically. Read along, or hit <strong>Play Narration</strong> to
                have the story read aloud.
              </Step>
              <Tip>On the Free plan you get 5 stories per month. Upgrade to Basic or Family for more.</Tip>
            </div>

            {/* ─── INSTALL PWA ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="install-pwa" icon="bi-phone-fill" title="Install the App (PWA)"
                subtitle="Add Dreamy Bedtime to your home screen for a full app experience — no app store needed." />

              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Dreamy Bedtime is a <strong>Progressive Web App (PWA)</strong>. This means it works like a native app
                on your phone or tablet without requiring a download from the App Store or Google Play. You install it
                directly from your browser in seconds.
              </p>

              <H3>iPhone & iPad (Safari)</H3>
              <Step n={1}>Open <strong>Safari</strong> and go to the Dreamy Bedtime website. Chrome on iOS does not
                support PWA installation — use Safari.</Step>
              <Step n={2}>Tap the <strong>Share</strong> button at the bottom of the screen (the box with an arrow
                pointing up).</Step>
              <Step n={3}>Scroll down in the share sheet and tap <strong>Add to Home Screen</strong>.</Step>
              <Step n={4}>Edit the name if you like, then tap <strong>Add</strong> in the top-right corner.</Step>
              <Step n={5}>The Dreamy Bedtime icon will appear on your home screen. Open it from there for the
                full-screen app experience.</Step>
              <Warning>
                Photos and audio work slightly differently on iOS PWA. If a photo upload shows "please upload a child
                photo", try using the front-facing camera directly rather than selecting from your gallery.
              </Warning>

              <H3>Android (Chrome or Samsung Internet)</H3>
              <Step n={1}>Open <strong>Chrome</strong> and visit the Dreamy Bedtime website.</Step>
              <Step n={2}>Tap the <strong>three-dot menu</strong> (⋮) in the top-right corner.</Step>
              <Step n={3}>Tap <strong>Add to Home screen</strong> or <strong>Install app</strong>.</Step>
              <Step n={4}>Confirm by tapping <strong>Add</strong> or <strong>Install</strong>. The app icon will
                appear on your home screen.</Step>
              <Note>Samsung Internet users: tap the menu icon → <strong>Add page to</strong> → <strong>Home screen</strong>.</Note>

              <H3>Desktop (Chrome or Edge)</H3>
              <Step n={1}>Visit the Dreamy Bedtime website in <strong>Chrome</strong> or <strong>Microsoft Edge</strong>.</Step>
              <Step n={2}>Look for the <strong>install icon</strong> in the address bar (a small computer screen with a
                + symbol) on the right side.</Step>
              <Step n={3}>Click it and confirm the installation. The app opens in its own window, separate from your
                browser.</Step>
              <Step n={4}>On Mac with Safari, choose <strong>File → Add to Dock</strong> (Safari 17+).</Step>

              <H3>Benefits of Installing</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2, paddingLeft: 20 }}>
                <li>Opens fullscreen — no browser address bar</li>
                <li>Works faster with cached resources</li>
                <li>Receive <strong>push notifications</strong> for bedtime reminders (Android/Desktop)</li>
                <li>Better audio playback and microphone access for voice recording</li>
                <li>Feels like a native app on any device</li>
              </ul>
            </div>

            {/* ─── CHILD PROFILES ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="child-profiles" icon="bi-people-fill" title="Child Profiles"
                subtitle="Child profiles are the heart of personalisation — the richer the profile, the better the stories." />

              <H3>Creating a Profile</H3>
              <Step n={1} title="Go to Child Profiles">
                Click <strong>Child Profiles</strong> in the sidebar or tap <i className="bi bi-people-fill"></i> from
                the dashboard.
              </Step>
              <Step n={2} title="Click Add Child">
                Hit the <strong>+ Add Child</strong> button at the top of the page.
              </Step>
              <Step n={3} title="Fill in the basics">
                Enter your child's <strong>name</strong>, <strong>age</strong>, and <strong>gender</strong>. These are
                used to choose age-appropriate vocabulary, story complexity, and pronoun usage.
              </Step>
              <Step n={4} title="Save">
                Click <strong>Save Profile</strong>. You can always come back and edit any details.
              </Step>

              <H3>Interests</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Choose up to 8 topics your child loves — dinosaurs, space, animals, football, princesses, robots, etc.
                These <strong>drive the setting and plot</strong> of every generated story, not just appear as passing
                mentions. A child who loves dinosaurs and football will get stories where those things actually matter
                to the plot.
              </p>
              <Tip>Be specific. "Ocean animals" produces richer stories than just "animals". Mix two very different interests for surprising, creative stories.</Tip>

              <H3>Life Lessons & Themes</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Select values or lessons you'd like stories to quietly reinforce — kindness, bravery, sharing,
                perseverance, honesty. The AI weaves these into the story's emotional backbone through character
                actions, not by stating them outright.
              </p>

              <H3>Story Preferences</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2.1, paddingLeft: 20 }}>
                <li><strong>Story Length:</strong> Short (3–4 min read), Medium (5–7 min), Long (8–10 min)</li>
                <li><strong>Story Tone:</strong> Magical, Adventurous, Funny, Calm, Educational, Cozy, Exciting, Spooky</li>
                <li><strong>Language:</strong> English, Spanish, French, Hindi, German, Portuguese, Arabic, Chinese,
                  Japanese, Italian, Gujarati</li>
              </ul>

              <H3>Profile Photo</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Upload a clear photo of your child's face. This photo is used for <strong>Photo Illustrations</strong>
                — the AI places your child's face into storybook illustrations. The photo is stored securely and only
                used within your account.
              </p>
              <Tip icon="bi-camera-fill">
                Use a clear, forward-facing photo in good lighting. Avoid sunglasses, hats, or group photos. The AI
                needs a clear view of the face to generate accurate illustrations.
              </Tip>

              <H3>Editing & Deleting a Profile</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Tap any child's card to expand it, then use the <strong>Edit</strong> button to update any details.
                To delete a profile, scroll to the bottom of the edit form and click <strong>Delete Profile</strong>.
                Note: deleting a profile does not delete the stories already generated for that child.
              </p>
            </div>

            {/* ─── GENERATING STORIES ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="generate-stories" icon="bi-magic" title="Generating Stories"
                subtitle="Creating a personalised bedtime story takes about 15–30 seconds." />

              <H3>How Story Generation Works</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                When you request a story, our AI passes through a three-stage process: it first generates a complete
                draft story, then refines the prose for immersiveness, then adjusts the length to hit your target
                word count precisely. The result is then saved to your library and sent to your Story Reader.
              </p>

              <H3>Step-by-Step</H3>
              <Step n={1} title="Select a child">
                Choose which child the story is for. The app pulls in their interests, lessons, age, preferred tone
                and length automatically.
              </Step>
              <Step n={2} title="Optional: customise the story">
                You can optionally set:
                <ul style={{ marginTop: 6, lineHeight: 2 }}>
                  <li><strong>Occasion</strong> — birthday, Christmas, first day of school, etc.</li>
                  <li><strong>Override tone</strong> — e.g. force a calming tone for a fussy night</li>
                  <li><strong>Override length</strong> — quick short story or full long adventure</li>
                  <li><strong>Language</strong> — override the child's default language</li>
                </ul>
              </Step>
              <Step n={3} title="Click Generate">
                The request is sent instantly. You're taken to the Story Reader, which shows a moon-and-stars loading
                screen with status messages while the story is being written in the background.
              </Step>
              <Step n={4} title="Story appears automatically">
                When generation is complete, the story fades in. No need to refresh — the page polls automatically
                every few seconds.
              </Step>
              <Note>
                If Approval Mode is on (see Story Approvals), the story will go to your Approval Queue instead of
                appearing immediately in the library.
              </Note>

              <H3>Story Credits</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Each story uses <strong>one story credit</strong>. Credits reset on the 1st of every month. If a story
                fails to generate, your credit is automatically refunded — you are never charged for a failed
                generation.
              </p>

              <H3>Monthly Limits by Plan</H3>
              <div className="table-responsive">
                <table className="table table-sm" style={{ fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--page-bg)' }}>
                      <th style={{ color: 'var(--text-main)' }}>Feature</th>
                      <th className="text-center" style={{ color: 'var(--text-muted2)' }}>Free</th>
                      <th className="text-center" style={{ color: '#6f42c1' }}>Basic</th>
                      <th className="text-center" style={{ color: '#e65c00' }}>Family</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableRow label="Stories per month" free="5" basic="60" family="120" />
                    <TableRow label="Child profiles" free="1" basic="3" family="Unlimited" />
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─── STORY LIBRARY ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="story-library" icon="bi-book-half" title="Story Library"
                subtitle="All your generated stories in one place, organised and searchable." />

              <H3>Finding Your Stories</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Go to <strong>Story Library</strong> in the sidebar. All completed stories are shown as cards sorted
                by most recent first. Stories that are still generating do not appear until they're ready.
              </p>

              <H3>Filtering by Child</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Use the child filter tabs at the top of the library to show only stories for a specific child. This is
                especially useful in families with multiple children.
              </p>

              <H3>Favourites</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Tap the <i className="bi bi-heart"></i> heart icon on any story card to mark it as a favourite. You
                can filter the library to show only favourites — perfect for when your child asks for "that one story"
                again.
              </p>

              <H3>Story Cards</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Each card shows the story title, which child it's for, reading time, how many times it's been read,
                and the illustration if one has been generated. Click any card to open the Story Reader.
              </p>
            </div>

            {/* ─── READING STORIES ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="reading-stories" icon="bi-book-fill" title="Reading Stories"
                subtitle="The Story Reader is designed for a calm, distraction-free bedtime experience." />

              <H3>Story Reader Controls</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2.2, paddingLeft: 20 }}>
                <li><i className="bi bi-play-circle-fill me-2" style={{ color: '#6f42c1' }}></i>
                  <strong>Play Narration</strong> — Start AI or cloned voice narration</li>
                <li><i className="bi bi-heart me-2" style={{ color: '#6f42c1' }}></i>
                  <strong>Favourite</strong> — Add to favourites</li>
                <li><i className="bi bi-share me-2" style={{ color: '#6f42c1' }}></i>
                  <strong>Share</strong> — Generate a shareable link (valid for 7 days)</li>
                <li><i className="bi bi-arrow-left me-2" style={{ color: '#6f42c1' }}></i>
                  <strong>Back</strong> — Return to the Story Library</li>
              </ul>

              <H3>Sharing a Story</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Click <strong>Share</strong> to generate a public link that anyone can use to read the story — without
                needing a Dreamy Bedtime account. Great for sending to grandparents or other family members. The link
                expires after 7 days.
              </p>

              <H3>Quiz (Interactive Learning)</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                After reading, tap <strong>Take a Quiz</strong> to generate 3 recall questions based on the story
                content. This is a fun way to reinforce comprehension and check if your child was paying attention.
              </p>

              <H3>Mark as Read</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Opening a story automatically marks it as read and increments your child's reading count, which
                appears in Reading Reports. Stories can be read any number of times — read counts accumulate.
              </p>
            </div>

            {/* ─── AI NARRATION ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="ai-narration" icon="bi-headphones" title="AI Narration"
                subtitle="Have any story read aloud in a warm, natural voice." />
              <PlanBadge plan="basic" /> <PlanBadge plan="family" />
              <div className="mt-3" />

              <H3>How It Works</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                AI Narration uses OpenAI's text-to-speech engine to read your story aloud in a clear, warm voice.
                The story is split into sections, each generated separately and joined into a single continuous
                audio file. The audio is cached after the first play — subsequent plays are instant and use no
                additional credits.
              </p>

              <H3>Playing Narration</H3>
              <Step n={1}>Open any story in the Story Reader.</Step>
              <Step n={2}>Click the <strong>Play Narration</strong> button. The first play may take 10–20 seconds
                to generate the audio.</Step>
              <Step n={3}>The audio plays directly in the browser. You can pause, resume, or stop at any time.</Step>
              <Note>
                Narration is cached after the first generation. Every time you play it after that, it loads
                instantly from storage — no additional credits are used.
              </Note>

              <H3>Narration Credits</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Each unique story narration uses one narration credit. Credits reset monthly. If narration fails
                partway through, your credit is automatically refunded.
              </p>
              <div className="table-responsive mt-2">
                <table className="table table-sm" style={{ fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--page-bg)' }}>
                      <th style={{ color: 'var(--text-main)' }}>Feature</th>
                      <th className="text-center" style={{ color: 'var(--text-muted2)' }}>Free</th>
                      <th className="text-center" style={{ color: '#6f42c1' }}>Basic</th>
                      <th className="text-center" style={{ color: '#e65c00' }}>Family</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableRow label="AI Narrations/month" free={<Cross />} basic="30" family="40" />
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─── VOICE CLONING ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="voice-cloning" icon="bi-mic-fill" title="Voice Cloning"
                subtitle="Record your own voice and have every story narrated in it — as if you're reading to your child." />
              <PlanBadge plan="family" />
              <div className="mt-3" />

              <H3>How It Works</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Voice Cloning is an exclusive Family plan feature. You record at least 10 seconds of your own
                voice, and the app creates a voice model from it using Cartesia's Instant Voice Cloning technology.
                From then on, any story can be narrated in <em>your voice</em> — not an AI voice, but one that
                sounds like you.
              </p>

              <H3>Recording Requirements</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2.1, paddingLeft: 20 }}>
                <li>At least <strong>10 seconds</strong> of clear speech</li>
                <li>Record in a <strong>quiet room</strong> — minimal background noise</li>
                <li>Speak at a <strong>natural pace</strong> — not too fast or too slow</li>
                <li>Use the device's <strong>microphone directly</strong> (not speakerphone)</li>
                <li>Any content works — read a book passage, describe your day, etc.</li>
              </ul>
              <Tip icon="bi-mic-fill">
                Read 2–3 paragraphs from a book for the best results. Varied vocabulary and natural sentence rhythm
                helps the voice model capture your speaking style accurately.
              </Tip>

              <H3>Creating Your Voice Profile</H3>
              <Step n={1}>Go to <strong>Settings</strong> and find the <strong>My Voice</strong> section.</Step>
              <Step n={2}>Click <strong>Start Recording</strong>. Allow microphone access when prompted.</Step>
              <Step n={3}>Speak clearly for at least 10 seconds, then click <strong>Stop</strong>.</Step>
              <Step n={4}>Review your recording and click <strong>Upload & Create Voice</strong>.</Step>
              <Step n={5}>Processing takes a few seconds. Once complete, your voice profile shows as ready.</Step>

              <H3>Using Your Voice for Narration</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Open any story in the Story Reader and click <strong>Play with My Voice</strong>. The first time
                takes 15–30 seconds to generate. Subsequent plays are instant.
              </p>

              <H3>Managing Your Voice Profile</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                You can re-record your voice at any time in Settings → My Voice. Uploading a new recording
                replaces the old one. To remove your voice profile entirely, click <strong>Delete Voice</strong>
                — this also removes the voice model from our systems.
              </p>

              <H3>Clone Narration Credits</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Each story narrated with your cloned voice uses one clone narration credit per month. These reset
                monthly. After the first generation, the audio is cached and replaying it uses no additional credits.
              </p>
            </div>

            {/* ─── PHOTO ILLUSTRATIONS ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="photo-illustrations" icon="bi-image-fill" title="Photo Illustrations"
                subtitle="AI-generated storybook art featuring your child's actual face." />

              <H3>How It Works</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                When you upload a child's photo and enable <strong>Use Photo in Illustrations</strong>, every
                generated story gets a custom illustration showing the story's key scene — with your child's face
                placed in it using AI face-swap technology. The result looks like storybook art starring your child.
              </p>

              <H3>Enabling Photo Illustrations</H3>
              <Step n={1}>Go to <strong>Child Profiles</strong> and open the child's profile.</Step>
              <Step n={2}>Click <strong>Upload Photo</strong> and choose a clear photo of the child's face.</Step>
              <Step n={3}>Toggle <strong>Use Photo in Illustrations</strong> to on.</Step>
              <Step n={4}>New stories generated for this child will automatically include a personalised
                illustration.</Step>

              <H3>Photo Requirements</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2.1, paddingLeft: 20 }}>
                <li>Clear, <strong>forward-facing</strong> shot of the face</li>
                <li>Good, even lighting — avoid harsh shadows or glare</li>
                <li>No sunglasses, hats, or masks</li>
                <li>Single person in the photo (no group photos)</li>
                <li>JPEG, PNG, HEIC formats accepted</li>
                <li>Minimum 400×400 pixels recommended</li>
              </ul>

              <H3>Regenerating an Illustration</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                If you're not happy with an illustration, open the story and click <strong>Regenerate
                Illustration</strong>. This uses one illustration credit and produces a new image. The old
                illustration is replaced.
              </p>

              <H3>Illustration Credits</H3>
              <div className="table-responsive mt-2">
                <table className="table table-sm" style={{ fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--page-bg)' }}>
                      <th style={{ color: 'var(--text-main)' }}>Feature</th>
                      <th className="text-center" style={{ color: 'var(--text-muted2)' }}>Free</th>
                      <th className="text-center" style={{ color: '#6f42c1' }}>Basic</th>
                      <th className="text-center" style={{ color: '#e65c00' }}>Family</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableRow label="Photo illustrations/month" free={<Cross />} basic="3" family="6" />
                    <TableRow label="Regen illustrations/month" free={<Cross />} basic="20" family="40" />
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─── APPROVAL QUEUE ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="approval-queue" icon="bi-eye-fill" title="Story Approvals"
                subtitle="Review every AI-generated story before it appears in your child's library." />

              <H3>What Is the Approval Queue?</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                When Approval Mode is enabled, every newly generated story is held in the <strong>Approval
                Queue</strong> rather than appearing immediately in the Story Library. You review the story,
                check it's appropriate, and then approve it (or reject it if something's off). This gives you
                complete control over what your child reads.
              </p>

              <H3>Enabling Approval Mode</H3>
              <Step n={1}>Go to <strong>Settings</strong>.</Step>
              <Step n={2}>Find the <strong>Story Approval</strong> setting and toggle it on.</Step>
              <Step n={3}>All future stories will go to the queue automatically. Existing stories are not affected.</Step>

              <H3>Reviewing a Story</H3>
              <Step n={1}>Click <strong>Approval Queue</strong> in the sidebar. A badge shows how many stories
                are waiting.</Step>
              <Step n={2}>Click any story to read it in full.</Step>
              <Step n={3}>Click <strong>Approve</strong> to add it to the Story Library, or <strong>Reject</strong>
                to discard it. Rejected stories are permanently deleted.</Step>
              <Note>
                Approving or rejecting does not affect credits — the credit was used at generation time regardless
                of the outcome.
              </Note>

              <H3>Skipping Approval</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                If you trust the AI and don't want the extra step, turn Approval Mode off. Stories will go
                directly to the library as soon as they're generated.
              </p>
            </div>

            {/* ─── STORY SERIES ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="story-series" icon="bi-collection-play-fill" title="Story Series"
                subtitle="Build a multi-chapter adventure that continues night after night." />

              <H3>What Is a Series?</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                A Story Series is a collection of connected chapters — like a chapter book — where each night
                picks up exactly where the last one ended. Characters develop over chapters, plot threads carry
                forward, and the AI remembers exactly what happened to keep the story consistent.
              </p>

              <H3>Creating a Series</H3>
              <Step n={1}>Go to <strong>Story Series</strong> in the sidebar and click <strong>New Series</strong>.</Step>
              <Step n={2}>Give the series a <strong>title</strong> and <strong>description</strong>.</Step>
              <Step n={3}>Set the <strong>tone, setting, and recurring characters</strong>. These persist across
                all chapters.</Step>
              <Step n={4}>Select the <strong>child</strong> this series is for.</Step>
              <Step n={5}>Click <strong>Create Series</strong>. The series is set up — no chapter is generated
                yet.</Step>

              <H3>Generating Chapters</H3>
              <Step n={1}>Open the series from the Series page.</Step>
              <Step n={2}>Click <strong>Generate Chapter 1</strong>. This uses one story credit and creates the
                opening chapter.</Step>
              <Step n={3}>Read Chapter 1, then return to the series and click <strong>Generate Next
                Chapter</strong>. Each chapter uses one story credit.</Step>
              <Step n={4}>When the story is complete, click <strong>Mark as Finished</strong> to close the
                series.</Step>
              <Tip>
                Let a few days pass between chapters to build anticipation. The AI keeps detailed notes so it
                can pick up the exact moment where you left off, even weeks later.
              </Tip>

              <H3>Series Management</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Edit the series title, description, characters, or setting at any time from the Series detail
                page. You can also delete a series, which removes all its chapters.
              </p>
            </div>

            {/* ─── COMMUNITY ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="community" icon="bi-globe2" title="Community Stories"
                subtitle="Browse, read, and share stories with other families." />

              <H3>Browsing Community Stories</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                The <strong>Community</strong> section (accessible from the sidebar or top navigation) shows stories
                that families have chosen to share publicly. Browse by theme, language, or age group. All community
                stories are read-only — you can't edit them, but you can read them for free without using any credits.
              </p>

              <H3>Sharing Your Story</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Open any story in the Story Reader and click the <strong>Share to Community</strong> option. The
                story's title, content, and illustration will be listed publicly. Your name and child's identity
                are not revealed — stories are attributed anonymously.
              </p>

              <H3>Removing a Story</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                You can remove a story from the community at any time by opening the story and selecting
                <strong> Remove from Community</strong>. It will no longer be visible to other users.
              </p>
            </div>

            {/* ─── READING REPORTS ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="reading-reports" icon="bi-bar-chart-fill" title="Reading Reports"
                subtitle="Track your child's reading habits and celebrate their milestones." />

              <H3>Accessing Reports</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Go to <strong>Reading Reports</strong> in the sidebar. Choose a child from the tabs at the top.
                You can also access a child's individual report directly from their profile card on the Children
                page.
              </p>

              <H3>What's Tracked</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2.1, paddingLeft: 20 }}>
                <li><strong>Stories read</strong> — total count and per-week breakdown</li>
                <li><strong>Reading time</strong> — cumulative minutes spent reading</li>
                <li><strong>Reading streaks</strong> — consecutive days with at least one story read</li>
                <li><strong>Favourite genres</strong> — which story tones and themes are most popular</li>
                <li><strong>Most read stories</strong> — stories your child returns to again and again</li>
                <li><strong>Language breakdown</strong> — for multilingual families</li>
              </ul>

              <H3>Weekly Digest</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Enable the <strong>Weekly Digest</strong> email in Settings to receive a summary of reading activity
                every week. You can choose which day the digest is sent.
              </p>

              <H3>Milestones</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Reports show milestone badges when your child reaches reading goals — first story, 10 stories, 25
                stories, and so on. These are a great way to celebrate reading habits with your child.
              </p>
            </div>

            {/* ─── NOTIFICATIONS ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="notifications" icon="bi-bell-fill" title="Notifications"
                subtitle="Bedtime reminders and weekly updates, exactly when you need them." />

              <H3>Bedtime Reminders</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Set a daily reminder to prompt you when it's story time. You can receive reminders via
                <strong> email</strong> and/or <strong>push notification</strong> (if you've installed the PWA
                and granted notification permission).
              </p>
              <Step n={1}>Go to <strong>Settings → Bedtime Reminders</strong>.</Step>
              <Step n={2}>Choose your <strong>reminder time</strong> and <strong>timezone</strong>.</Step>
              <Step n={3}>Toggle <strong>Email Reminder</strong> and/or <strong>Push Notification</strong> on.</Step>
              <Step n={4}>Click <strong>Save</strong>. You'll receive reminders at the set time every day.</Step>
              <Step n={5}>Use <strong>Send Test Reminder</strong> to confirm everything is working.</Step>

              <H3>Push Notification Setup</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Push notifications require the app to be installed as a PWA (see Install the App section) and
                for you to allow notifications when the browser prompts. On iOS, push notifications require
                iOS 16.4+ and the app installed to the home screen via Safari.
              </p>
              <Warning>
                If you don't see a notification permission prompt, check your browser or device settings and
                ensure notifications are not blocked for this site.
              </Warning>

              <H3>Weekly Digest Email</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                The weekly digest is an email summary of reading activity across all your children.
              </p>
              <Step n={1}>Go to <strong>Settings → Weekly Digest</strong>.</Step>
              <Step n={2}>Toggle the digest on and choose which <strong>day of the week</strong> you'd like
                to receive it.</Step>
              <Step n={3}>Click <strong>Save</strong>.</Step>
              <Note>Use <strong>Send Test Digest</strong> to preview what the email looks like before enabling it.</Note>

              <H3>Marketing Emails</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Opt in or out of product update emails, tips, and news from Dreamy Bedtime via the
                <strong> Marketing Emails</strong> toggle in Settings. This does not affect transactional emails
                like receipts or password resets.
              </p>
            </div>

            {/* ─── ACCOUNT MANAGEMENT ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="account-management" icon="bi-person-fill" title="Account Management"
                subtitle="Manage your profile, subscription, billing, and security." />

              <H3>Updating Your Profile</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Go to <strong>Settings → Profile</strong> to update your name or email address. Changes take
                effect immediately.
              </p>

              <H3>Subscription Plans</H3>
              <div className="table-responsive mt-2 mb-3">
                <table className="table table-sm" style={{ fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--page-bg)' }}>
                      <th style={{ color: 'var(--text-main)' }}>Feature</th>
                      <th className="text-center" style={{ color: 'var(--text-muted2)' }}>Free</th>
                      <th className="text-center" style={{ color: '#6f42c1' }}>Basic</th>
                      <th className="text-center" style={{ color: '#e65c00' }}>Family</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableRow label="Stories/month" free="5" basic="60" family="120" />
                    <TableRow label="AI Narrations/month" free={<Cross />} basic="30" family="40" />
                    <TableRow label="Child profiles" free="1" basic="3" family="Unlimited" />
                    <TableRow label="Story Series" free={<Cross />} basic={<Check />} family={<Check />} />
                    <TableRow label="Photo Illustrations" free={<Cross />} basic="3/mo" family="6/mo" />
                    <TableRow label="Voice Cloning" free={<Cross />} basic={<Cross />} family={<Check />} />
                    <TableRow label="Clone Narrations/month" free={<Cross />} basic={<Cross />} family="5" />
                    <TableRow label="Community Access" free={<Check />} basic={<Check />} family={<Check />} />
                  </tbody>
                </table>
              </div>

              <H3>Upgrading or Downgrading</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                {paymentStoreName() ? (
                  <>Choose a different plan on the <strong>Pricing</strong> page to upgrade or downgrade, and
                  {' '}{paymentStoreName()} handles the change. To cancel, go to <strong>Settings → Subscription</strong>
                  {' '}and tap <strong>Manage Subscription</strong>, which opens your {paymentStoreName()} subscriptions.</>
                ) : (
                  <>Go to <strong>Settings → Subscription</strong> and click <strong>Manage Subscription</strong>.
                  You'll be taken to the Stripe customer portal where you can upgrade, downgrade, or cancel. Changes
                  take effect at the start of your next billing period.</>
                )}
              </p>
              <Note>
                Stories already in your library are always accessible, even if you downgrade. You keep everything
                you've generated.
              </Note>

              <H3>Billing & Receipts</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                {paymentStoreName() ? (
                  <>Purchases made in the app are billed by <strong>{paymentStoreName()}</strong>, which sends your
                  receipts and keeps your purchase history in your account there.</>
                ) : (
                  <>All billing is handled by <strong>Stripe</strong>. Access your invoices and billing history via
                  the Stripe customer portal (Settings → Subscription → Manage Subscription). Receipts are also
                  emailed to you automatically after each charge.</>
                )}
              </p>

              <H3>Signing Out</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Click your name at the bottom of the sidebar, then <strong>Sign Out</strong>. To sign out of all
                devices (e.g. if you've lost a device), use <strong>Sign Out All Devices</strong> in
                Settings → Account.
              </p>

              <H3>Deleting Your Account</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Account deletion is permanent and removes all your stories, child profiles, and voice data.
                Go to <strong>Settings → Account</strong> and click <strong>Delete Account</strong>. You'll be
                asked to confirm. Active subscriptions are cancelled automatically.
              </p>
              <Warning>
                Account deletion cannot be undone. Download any stories you want to keep before deleting.
              </Warning>
            </div>

            {/* ─── CREDITS & PLANS ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="credits-plans" icon="bi-credit-card-fill" title="Credits & Plans"
                subtitle="How monthly credits work, what happens when you run out, and how top-up packs work." />

              <H3>How Monthly Credits Work</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Each plan includes a set number of credits per type (stories, narrations, etc.) that reset on the
                1st of every month. Credits do <strong>not</strong> roll over to the next month — use them or
                lose them.
              </p>

              <H3>Credit Types</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2.1, paddingLeft: 20 }}>
                <li><strong>Story credits</strong> — used each time you generate a story or a series chapter</li>
                <li><strong>Narration credits</strong> — used the first time a story is narrated with AI voice</li>
                <li><strong>Clone narration credits</strong> — used the first time a story is narrated with your
                  cloned voice (Family plan)</li>
                <li><strong>Photo illustration credits</strong> — used when a story illustration is generated
                  for the first time</li>
                <li><strong>Regen illustration credits</strong> — used when you manually regenerate an
                  illustration</li>
              </ul>

              <H3>Automatic Credit Refunds</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                If any generation fails (story, narration, or illustration), your credit is <strong>automatically
                refunded</strong> — you are never charged for a failed generation. Refunds are instant and require
                no action from you.
              </p>

              <H3>Top-Up Credit Packs</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                If you run out of credits before the month resets, you can purchase additional top-up credit packs.
                These do not expire — unused top-up credits carry over to the next month, unlike monthly credits.
              </p>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Visit the <Link to="/pricing" style={{ color: '#6f42c1' }}>Pricing page</Link> to see available
                top-up packs. You can also buy packs directly from the error message that appears when a limit
                is reached.
              </p>
            </div>

            {/* ─── REFERRAL PROGRAM ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="referral-program" icon="bi-gift-fill" title="Referral Program"
                subtitle="Share Dreamy Bedtime with other families and earn bonus stories." />

              <H3>How It Works</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Every account has a unique referral link. When a friend signs up using your link and activates
                their account, you both receive a bonus — extra story credits added to your next month's allowance.
              </p>

              <H3>Your Referral Link</H3>
              <Step n={1}>Go to <strong>Settings → Referral</strong> or click <strong>Referral Program</strong>
                from the sidebar.</Step>
              <Step n={2}>Copy your unique referral link or use the share buttons to send it via WhatsApp,
                email, or social media.</Step>
              <Step n={3}>When a friend signs up and verifies their email, the bonus is applied
                automatically.</Step>

              <H3>Tracking Referrals</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                Your referral dashboard shows how many people have clicked your link, how many have signed up,
                and how many bonuses you've earned. There's no limit to how many people you can refer.
              </p>
            </div>

            {/* ─── GIFT STORIES ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="gift-stories" icon="bi-envelope-heart-fill" title="Gift Stories"
                subtitle="Send a personalised AI bedtime story as a gift — no account needed for the recipient." />

              <H3>Sending a Gift</H3>
              <Step n={1}>Go to the <strong>Gift a Story</strong> page (link in the top navigation).</Step>
              <Step n={2}>Enter the <strong>child's name, age, and occasion</strong> (birthday, Christmas, etc.).</Step>
              <Step n={3}>Add any extra details — interests, characters, special message from you.</Step>
              <Step n={4}>Complete the payment. The gift is processed and a story is generated.</Step>
              <Step n={5}>You receive a <strong>gift link</strong> to share with the recipient. Send it via
                email, WhatsApp, or any messaging app.</Step>

              <H3>Redeeming a Gift</H3>
              <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.7 }}>
                The recipient opens the gift link and reads the personalised story in a beautiful, distraction-free
                reader — <strong>no account required</strong>. They can also listen to an AI narration of the story.
              </p>
              <Note>
                Gift stories are generated once and the link stays active permanently. Recipients can read and replay
                the story any number of times.
              </Note>
            </div>

            {/* ─── TIPS ─── */}
            <div className="card border-0 shadow-sm mb-4 p-4" style={{ borderRadius: 16 }}>
              <SectionHeader id="tips" icon="bi-lightbulb-fill" title="Tips & Best Practices"
                subtitle="Get the most out of every Dreamy Bedtime feature." />

              <H3>Getting the Best Stories</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2.2, paddingLeft: 20 }}>
                <li>Choose <strong>2–4 specific interests</strong> rather than many broad ones. "Underwater robots"
                  produces a richer story than "robots" + "ocean" + "adventure" + "science".</li>
                <li>Mix a <strong>calm tone with an exciting interest</strong> for the ideal bedtime balance — the
                  story is engaging but the ending winds down gently.</li>
                <li>Use <strong>lesson themes</strong> to reinforce real values your child is working on at school
                  or home right now.</li>
                <li>Set the <strong>occasion</strong> field for upcoming events — the story will weave it in
                  naturally rather than forcing it.</li>
                <li>Try a <strong>Story Series</strong> for the Friday-night experience where they actually look
                  forward to what happens next.</li>
              </ul>

              <H3>Best Photos for Illustrations</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2.2, paddingLeft: 20 }}>
                <li>Use a <strong>recent photo</strong> taken in the last year — faces change quickly in young
                  children.</li>
                <li>Natural daylight works best. Avoid flash or direct overhead lighting.</li>
                <li>A <strong>slight smile</strong> or neutral expression produces the most versatile results
                  across different story scenes.</li>
                <li>Portrait orientation (phone held vertically) gives more face area per pixel.</li>
                <li>If the illustration doesn't look right, use <strong>Regenerate Illustration</strong> — each
                  attempt produces a different composition.</li>
              </ul>

              <H3>Best Voice Recordings</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2.2, paddingLeft: 20 }}>
                <li>Record in a <strong>small, quiet room</strong>. Bathrooms echo; closets with clothes dampen
                  sound nicely.</li>
                <li>Hold the device <strong>20–30 cm from your mouth</strong> — not directly in front where
                  breath noise is captured.</li>
                <li>Record at least <strong>20–30 seconds</strong> for the best voice model accuracy.</li>
                <li>Read naturally. The AI captures your <em>speaking rhythm</em>, not just your voice tone.</li>
                <li>If the clone doesn't sound like you, try re-recording in a quieter space.</li>
              </ul>

              <H3>Managing Credits Efficiently</H3>
              <ul style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 2.2, paddingLeft: 20 }}>
                <li>Narrations are <strong>cached after the first play</strong> — replaying a narrated story is
                  always free.</li>
                <li>Use <strong>Story Series</strong> to get more value from credits — chapters are shorter but
                  feel part of a bigger adventure.</li>
                <li>Enable <strong>Weekly Digest</strong> to track credit usage without logging in daily.</li>
                <li><strong>Top-up credits never expire</strong> — buy them when you run low mid-month and they
                  carry forward if unused.</li>
              </ul>

              <div className="rounded-3 p-4 mt-3 text-center"
                style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🌙</div>
                <div className="fw-bold text-white mb-1" style={{ fontSize: 16 }}>Still have questions?</div>
                <p className="text-white mb-3" style={{ opacity: 0.85, fontSize: 14 }}>
                  Our support team is here to help. Reach out any time.
                </p>
                <a href="mailto:support@dreamybedtime.com"
                  className="btn btn-light rounded-pill px-4 fw-semibold"
                  style={{ color: '#6f42c1', fontSize: 14 }}>
                  <i className="bi bi-envelope me-2"></i>Contact Support
                </a>
              </div>
            </div>

          </div>{/* /col */}
        </div>{/* /row */}
      </div>{/* /container */}

      {/* Footer */}
      <div style={{ background: 'var(--page-bg)', borderTop: '1px solid #e8d5ff', padding: '32px 0' }}>
        <div className="container text-center">
          <div className="mb-2" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 20 }}>
            🌙 Dreamy Bedtime
          </div>
          <div className="d-flex justify-content-center gap-4 flex-wrap">
            {[
              { to: '/pricing', label: 'Pricing' },
              { to: '/blog', label: 'Blog' },
              { to: '/contact', label: 'Contact' },
              { to: '/privacy', label: 'Privacy' },
              { to: '/terms', label: 'Terms' },
            ].map(l => (
              <Link key={l.to} to={l.to}
                style={{ color: '#6f42c1', fontSize: 13, textDecoration: 'none', fontWeight: 500 }}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default GuidePage;
